import { fetchData } from '@umu/umu-fetch';
import { defaultList } from '../dataFlow';
import {
    ICategory,
    ICategoryParam,
    IDataInfo,
    IPracticeChannelParam,
    ISearchMemberInfo,
    ISearchParams,
    ISearchUserRes,
} from '../interface';
import { API } from '../api';
import { API as SEARCH_API } from '../../../services/userSearch';

/**
 * 获取课程列表
 */
const getCourseList = (params: ISearchParams): Promise<IDataInfo> => {
    return fetchData(API.GET_COURSE_LIST, params, { silent: true }).then(
        (res: IResponse<IDataInfo>) => {
            return res.data || defaultList;
        },
        () => {
            return defaultList;
        },
    );
};
/**
 * 获取学习项目列表
 */
const getProgramList = (params: ISearchParams): Promise<IDataInfo> => {
    return fetchData(API.GET_PROGRAM_LIST, params, { silent: true }).then(
        (res: IResponse<IDataInfo>) => {
            return res.data || defaultList;
        },
        () => {
            return defaultList;
        },
    );
};
/**
 * 获取小节列表
 */
const getSessionList = (params: ISearchParams): Promise<IDataInfo> => {
    return fetchData(API.GET_SESSION_LIST, params, { silent: true }).then(
        (res: IResponse<IDataInfo>) => {
            return res.data || defaultList;
        },
        () => {
            return defaultList;
        },
    );
};
/**
 * 获取讲师列表
 */
const getLecturerList = (params: ISearchParams): Promise<IDataInfo> => {
    const searchCondition: any = {};
    if (params.uids) {
        searchCondition.uids = [params.uids || ''];
    }
    if (params.enterprise_group_ids) {
        searchCondition.enterprise_group_ids = String(params.enterprise_group_ids).split(',');
    }
    if (params.department_ids) {
        searchCondition.department_ids = String(params.department_ids).split(',');
    }

    return fetchData(
        API.GET_LECTURER_LIST,
        {
            page: params.page,
            size: params.size,
            is_filter_by_enterprise_role: params.is_filter_by_enterprise_role || '0',
            search_condition: JSON.stringify(searchCondition),
        },
        { silent: true },
    ).then(
        (res: IResponse<IDataInfo>) => {
            return res.data || defaultList;
        },
        () => {
            return defaultList;
        },
    );
};
/**
 * 获取微页面列表
 */
const getMicroPagesList = (params: ISearchParams): Promise<IDataInfo> => {
    return fetchData(API.GET_MICRO_PAGES_LIST, params, { silent: true }).then(
        (res: IResponse<IDataInfo>) => {
            return res.data || defaultList;
        },
        () => {
            return defaultList;
        },
    );
};
/*
 * 获取分类数据
 */
const getCategoryList = (param: ICategoryParam): Promise<ICategory[]> => {
    return fetchData(API.GET_CATEGORY_LIST, param, { silent: true }).then(
        (res: IResponse<{ list: ICategory[] }>) => {
            return res.data ? res.data.list || [] : [];
        },
        () => {
            return [];
        },
    );
};

const getPracticeChannelList = (param: IPracticeChannelParam): Promise<IDataInfo[]> => {
    return fetchData(API.GET_PRACTICE_CHANNEL_LIST, param, { silent: true }).then(
        (res: IResponse<{ list: IDataInfo[] }>) => {
            return res.data || defaultList;
        },
        () => {
            return [];
        },
    );
};

export interface IRelationPractice {
    access_permission: string;
    channel_id: string;
    channel_m_url: string;
    channel_name: string;
    channel_url: string;
    color: string;
    desc: string;
    id: string;
    unique_id: string;
    is_publish: string;
    m_url: string;
    name: string;
    obj_id: string;
    obj_type: string;
    practice_obj_id: string;
    url: string;
}

const getCanRelationPracticeList = (param: {
    keyword?: string;
    page: number;
    size: number;
}): Promise<IRelationPractice[]> => {
    return fetchData(
        API.GET_PRACTICE_CAN_RELATION,
        {
            keyword: param.keyword || '',
            page: param.page,
            size: param.size,
            is_manage: 1,
            speaker_mode: '-1',
            obj_type: '-1',
        },
        { silent: true },
    ).then(
        (res: IResponse<{ list: IRelationPractice[] }>) => {
            const data = res.data || defaultList;
            data.list = data.list.map((item) => ({
                ...item,
                id: item.unique_id,
            }));
            return data;
        },
        () => {
            return defaultList;
        },
    );
};

/**
 * 用户搜索
 */
const searchUserList = (param: ISearchParams): Promise<ISearchUserRes> => {
    return fetchData(API.GET_USER_LIST, param, { silent: true }).then(
        (res: { data: ISearchUserRes }) => {
            return res.data;
        },
        () => {
            return defaultList;
        },
    );
};
/**
 * 分组搜索
 */
const searchGroupList = (param: ISearchParams): Promise<ISearchUserRes> => {
    return fetchData(API.GET_GROUP_LIST, param, { silent: true }).then(
        (res: { data: ISearchUserRes }) => {
            return res.data;
        },
        () => {
            return defaultList;
        },
    );
};
/**
 * 部门列表
 */
const getDepartmentList = (param: ISearchParams): Promise<ISearchUserRes> => {
    return fetchData(API.GET_DEPARTMENT_LIST, param, { silent: true }).then(
        (res: { data: ISearchUserRes }) => {
            return {
                list: res.data.department_list || [],
                page_info: defaultList.page_info,
            };
        },
        () => {
            return defaultList;
        },
    );
};
interface IMemberParams {
    accounts: string;
    micro_page_id?: string;
    is_suggestion: string;
    search_source: string;
}
const getMemberSearch = (params: IMemberParams) => {
    return fetchData(SEARCH_API.ACCOUNT_SEARCH_V2, params, { silent: true }).then(
        (res: IResponse<ISearchMemberInfo[]>) => {
            return res.data ? res.data : [];
        },
        () => {
            return [];
        },
    );
};
/**
 * 部门搜索
 */
const searchDepartmentList = (param: ISearchParams): Promise<ISearchUserRes> => {
    return fetchData(API.GET_DEPARTMENT_BY_SUG_LIST, param, { silent: true }).then(
        (res: { data: ISearchUserRes }) => {
            return {
                list: res.data || [],
                page_info: defaultList.page_info,
            };
        },
        () => {
            return defaultList;
        },
    );
};
/**
 * 获取消息统计信息 GET_UNREAD_COUNT
 */
interface IUnreadCountInfo {
    first_page: string;
    praise: string;
    private_message: string;
    reply: string;
    system: string;
    task: string;
    total_num: string;
}
const getUnreadCount = (): Promise<IUnreadCountInfo> => {
    const defaultCount = {
        first_page: '0',
        praise: '0',
        private_message: '0',
        reply: '0',
        system: '0',
        task: '0',
        total_num: '0',
    };
    return fetchData(API.GET_UNREAD_COUNT, {}, { silent: true }).then(
        (res: { data: IUnreadCountInfo }) => {
            return res.data || defaultCount;
        },
        () => {
            return defaultCount;
        },
    );
};
export {
    getCategoryList,
    searchUserList,
    searchGroupList,
    getDepartmentList,
    getMemberSearch,
    searchDepartmentList,
    getProgramList,
    getUnreadCount,
    getSessionList,
    getLecturerList,
    getMicroPagesList,
    getCourseList,
    getPracticeChannelList,
    getCanRelationPracticeList,
};
